import { formatBytes } from '@fountain/fountain-ui-components';
import axios from 'axios';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { REACT_APP_GLOBAL_API_BASE_URL_V2 } from 'runtimeEnvVars';

import dropzoneMessages from 'components/CustomDropzone/messages';
import { blobsChangedAction } from 'containers/ApplicantUploadFileModal/actions';
import {
  addErrorMessageFromCaughtValueI18n,
  addMessageAction,
} from 'containers/FlashMessage/actions';
import globalMessages from 'shared/global/messages';
import { apiGet, apiPost } from 'utils/axios';
import { rwxPreviewCredentials } from 'utils/domainMeta';

export default function useUploadFileToApplicant(options = {}) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { formatMessage } = intl;

  const [isUploading, setIsUploading] = useState(false);

  // A direct-to-storage upload (S3/Azure) returns the provider's own error
  // body on failure. An S3 XML error can include the AWS AccessKeyId (and
  // other request credentials); surfacing it verbatim in a FlashMessage leaks
  // secrets to the user. Replace any storage-provider failure with a generic,
  // translated message before it can propagate to the error handler.
  const throwSanitizedUploadError = () => {
    throw new Error(formatMessage(globalMessages.apiError));
  };

  // eslint-disable-next-line consistent-return
  const updateFileToApplicant = async ({ applicantId, key, file }) => {
    setIsUploading(true);
    const url = `${REACT_APP_GLOBAL_API_BASE_URL_V2}/applicants/${applicantId}/document_uploads`;
    try {
      const response = await apiGet(
        `${url}/presigned_blob?filename=${file.name}`,
      );
      const data = response.data.form_data;
      // Storage rejects a body over the signed limit with a provider error
      // the user only sees as the generic network message, so check first.
      const maxSize = response.data.max_size;
      if (maxSize && file.size > maxSize) {
        throw new Error(
          formatMessage(dropzoneMessages.fileTooBig, {
            maxSize: formatBytes(maxSize),
          }),
        );
      }

      const fileUploadBody = new FormData();
      /* Figure out if we are using AzureBlob for file storage */
      let isUsingAzureBlob = false;
      if (response.data && response.data.storage_provider === 'azure') {
        isUsingAzureBlob = true;
      }
      if (isUsingAzureBlob) {
        try {
          await axios.put(response.data.url, file, {
            headers: {
              'x-ms-blob-type': 'Blockblob',
              'Content-Type': 'binary/octet-stream',
            },
          });
        } catch (uploadError) {
          throwSanitizedUploadError();
        }
      } else {
        Object.keys(data).forEach(dataKey =>
          fileUploadBody.append(dataKey, data[dataKey]),
        );
        fileUploadBody.append(
          'Content-Type',
          file.type || 'application/octet-stream',
        );
        fileUploadBody.append('file', file);
        // Do not set the Content-Type header. With a FormData body the browser
        // must generate `multipart/form-data; boundary=...` itself; an explicit
        // (even empty-string) Content-Type suppresses the boundary in some
        // axios/browser combinations, so S3 receives an unparseable multipart
        // POST and rejects the upload.
        try {
          // A private RWX preview Hire's mock S3 admits only a credentialed
          // upload; every other host keeps the two-argument call.
          if (rwxPreviewCredentials(response.data.url) === 'include') {
            await axios.post(response.data.url, fileUploadBody, {
              withCredentials: true,
            });
          } else {
            await axios.post(response.data.url, fileUploadBody);
          }
        } catch (uploadError) {
          throwSanitizedUploadError();
        }
      }

      const finalBody = new FormData();
      finalBody.append(
        'document_upload[blob_meta]',
        JSON.stringify({
          blob_id: data.key,
          blob_size: file.size,
        }),
      );

      finalBody.append('document_upload[key]', key);
      const finalresponse = await apiPost(url, finalBody);
      if (!options.disableMessage) {
        dispatch(
          addMessageAction(
            formatMessage(globalMessages.updateFieldSuccess),
            'success',
          ),
        );
      }
      dispatch(blobsChangedAction());
      return finalresponse;
    } catch (e) {
      dispatch(addErrorMessageFromCaughtValueI18n(e, intl));
    } finally {
      setIsUploading(false);
    }
  };

  return { updateFileToApplicant, isUploading };
}
